'use client';

import { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import { COUNTRY_NAMES, DEFAULT_COUNTRY } from '@/lib/constants/countries';

export interface CountryFieldProps {
  id: string;
  /** Current country name. Blank renders as the default jurisdiction. */
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  /**
   * Geometry overrides for a non-page-form host (VALE-863) — the attribute
   * treatment's composite popovers. Passed in rather than named here for the
   * same layering reason `TextField`'s pair are; see that prop's note.
   */
  className?: string;
  labelClassName?: string;
  triggerClassName?: string;
}

/**
 * Country picker for an address — a searchable single-select over the canonical
 * {@link COUNTRY_NAMES} list.
 *
 * A picker rather than free text because `country` decides whether the sibling
 * `state` field is validated against the Australian state codes: a typo or an
 * inconsistent spelling silently changes that decision (LF-5933). Selecting from
 * a fixed list is what makes the stored value canonical by construction.
 *
 * Australia is pre-selected for a new address (it is ~99.9% of Vale's addresses),
 * so the common case is zero interaction — the search box exists for the rare
 * overseas beneficiary, not the default path.
 */
export function CountryField({
  id,
  value,
  onChange,
  required,
  className,
  labelClassName,
  triggerClassName
}: CountryFieldProps) {
  const [open, setOpen] = useState(false);

  // A blank country already *means* the default jurisdiction everywhere else, so show
  // it as such rather than an empty control the user has to think about.
  const selected = value.trim() || DEFAULT_COUNTRY;

  return (
    <div className={cn('space-y-2', className)}>
      <Label htmlFor={id} className={labelClassName}>
        Country
        {required && <span className="text-destructive ml-1">*</span>}
      </Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="ghost"
            role="combobox"
            aria-expanded={open}
            className={cn(
              'bg-muted/50 hover:bg-muted/70 h-auto w-full justify-between px-3 py-2 text-sm font-normal',
              triggerClassName
            )}
          >
            {selected}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0"
          align="start"
        >
          {/*
            Substring matching rather than cmdk's default fuzzy scorer, which ranks loose
            subsequence hits (typing "canad" also surfaces "Iceland"). Country names are
            short and known, so "contains" is both predictable and sufficient.
          */}
          <Command
            filter={(value, search) =>
              value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0
            }
          >
            <CommandInput placeholder="Search countries..." />
            <CommandList>
              <CommandEmpty>No country found.</CommandEmpty>
              <CommandGroup>
                {COUNTRY_NAMES.map((country) => (
                  <CommandItem
                    key={country}
                    value={country}
                    onSelect={() => {
                      onChange(country);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        selected === country ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    {country}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
